import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupLayout from "../SetupLayout/SetupLayout";
import { useSetupState, useSetupDispatch, setCompany, resetSetup } from "../../../auth/SetupContext";
import { API_BASE_URL } from "../../../auth/config";
import CustomDropdown from "../../../components/customComponents/customDropdown/CustomDropdown";
import formStyles from "../setupForm.module.scss";

const YES_NO_OPTIONS = [
  { id: "No", label: "No" },
  { id: "Yes", label: "Yes" },
];

const STATUS_OPTIONS = [
  { id: "1", label: "Active" },
  { id: "0", label: "Inactive" },
];

const initialForm = {
  name: "",
  addressLine1: "",
  addressLine2: "",
  location: "",
  email: "",
  phoneNumber: "",
  websiteUrl: "",
  companyLogo: "",
  gst: "",
  documentUrl: "",
  type: "",
  totalUser: "",
  onePercentPrivateLimited: "No",
  status: "1",
  backendUrl: "",
  defaultHours: "",
  approvedUserId: "",
  frontendUrl: "",
  createdUserId: "",
};

export default function CreateCompany() {
  const navigate = useNavigate();
  const dispatch = useSetupDispatch();
  const { clientId } = useSetupState();

  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!clientId) navigate("/setup/client", { replace: true });
  }, [clientId, navigate]);

  const handleBack = () => {
    navigate("/", { replace: true });
    setTimeout(() => dispatch(resetSetup()), 0);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : "Company name is required.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
          ? ""
          : "Please enter a valid email address.";
      case "phoneNumber":
        return value.trim() ? "" : "Phone number is required.";
      default:
        return "";
    }
  };

  const isFormValid =
    !validateField("name", form.name) &&
    !validateField("email", form.email) &&
    !validateField("phoneNumber", form.phoneNumber);

  const update = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const updateDropdown = (field) => (item) => {
    setForm((f) => ({ ...f, [field]: item.id }));
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, item.id) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const nextFieldErrors = {
      name: validateField("name", form.name),
      email: validateField("email", form.email),
      phoneNumber: validateField("phoneNumber", form.phoneNumber),
    };
    setFieldErrors(nextFieldErrors);

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setError("Name, email, and phone number are required.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        totalUser: form.totalUser ? Number(form.totalUser) : undefined,
        approvedUserId: form.approvedUserId ? Number(form.approvedUserId) : undefined,
        createdUserId: form.createdUserId ? Number(form.createdUserId) : undefined,
        status: Number(form.status),
        domainName: window.location.hostname,
        clientId: [clientId],
      };

      const res = await axios.post(`${API_BASE_URL}/api/companies`, payload);
      dispatch(setCompany(res.data));
      navigate("/setup/access", { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create company");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SetupLayout
      step={2}
      totalSteps={6}
      title="Create company"
      subtitle="Set up the company this client belongs to."
      error={error}
      onBack={handleBack}
      backDisabled={isSubmitting}
      loading={isSubmitting}
      loadingText="Creating company..."
    >
      <form onSubmit={handleSubmit} className={formStyles.body}>
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label}>Client ID</label>
          <div className={formStyles.readOnlyValue}>{clientId}</div>
        </div>

        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label} htmlFor="name">Company name</label>
          <input id="name" className={formStyles.input} value={form.name} onChange={update("name")} required />
          {fieldErrors.name && <span className={formStyles.fieldError}>{fieldErrors.name}</span>}
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="email">Email</label>
            <input id="email" type="email" className={formStyles.input} value={form.email} onChange={update("email")} required />
            {fieldErrors.email && <span className={formStyles.fieldError}>{fieldErrors.email}</span>}
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="phoneNumber">Phone number</label>
            <input id="phoneNumber" className={formStyles.input} value={form.phoneNumber} onChange={update("phoneNumber")} required />
            {fieldErrors.phoneNumber && <span className={formStyles.fieldError}>{fieldErrors.phoneNumber}</span>}
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="addressLine1">Address line 1</label>
            <input id="addressLine1" className={formStyles.input} value={form.addressLine1} onChange={update("addressLine1")} />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="addressLine2">Address line 2</label>
            <input id="addressLine2" className={formStyles.input} value={form.addressLine2} onChange={update("addressLine2")} />
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="location">Location</label>
            <input id="location" className={formStyles.input} value={form.location} onChange={update("location")} />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="websiteUrl">Website URL</label>
            <input id="websiteUrl" className={formStyles.input} value={form.websiteUrl} onChange={update("websiteUrl")} />
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="gst">GST</label>
            <input id="gst" className={formStyles.input} value={form.gst} onChange={update("gst")} />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="type">Type</label>
            <input id="type" className={formStyles.input} value={form.type} onChange={update("type")} />
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="totalUser">Total users</label>
            <input id="totalUser" type="number" className={formStyles.input} value={form.totalUser} onChange={update("totalUser")} />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label}>1% Private Limited</label>
            <CustomDropdown
              data={YES_NO_OPTIONS}
              value={form.onePercentPrivateLimited}
              className={formStyles.dropdownWrap}
              triggerClassName={formStyles.dropdownTrigger}
              showArrow
              onSelect={updateDropdown("onePercentPrivateLimited")}
            />
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="backendUrl">Backend URL</label>
            <input id="backendUrl" className={formStyles.input} value={form.backendUrl} onChange={update("backendUrl")} />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="frontendUrl">Frontend URL</label>
            <input id="frontendUrl" className={formStyles.input} value={form.frontendUrl} onChange={update("frontendUrl")} />
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="defaultHours">Default hours</label>
            <input id="defaultHours" className={formStyles.input} value={form.defaultHours} onChange={update("defaultHours")} />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label}>Status</label>
            <CustomDropdown
              data={STATUS_OPTIONS}
              value={form.status}
              className={formStyles.dropdownWrap}
              triggerClassName={formStyles.dropdownTrigger}
              showArrow
              onSelect={updateDropdown("status")}
            />
          </div>
        </div>

        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label}>Domain name</label>
          <div className={formStyles.readOnlyValue}>{window.location.hostname}</div>
        </div>

        <div className={formStyles.btnRow}>
          <button type="submit" className={formStyles.primaryBtn} disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? "Creating..." : "Create company"}
          </button>
        </div>
      </form>
    </SetupLayout>
  );
}
